import {ActionContext} from "vuex";
import {IAppState} from "@/interfaces/app-state.interface";
import {Vue} from "vue-property-decorator";
import {httpResource} from "@/resources/http.resource";
import {IApp} from "@/interfaces/app.interface";
import type {IParksConfig} from "@/modules/ParksConfig/interfaces/parks-config.interface";
import {IParksConfigResource} from "@/modules/ParksConfig/interfaces/parks-config-resource.interface";
import {IAppTable} from "@/shared/AppTable/interfaces/sort-table-data";
import {IParkEvent} from "@/modules/ParksConfig/interfaces/park-event.interface";
import {bus} from "@/plugins/bus";
import moment from "moment";

type ParksConfigStoreType = ActionContext<IParksConfig.IState, IAppState>;

export const ParksConfigStore: ParksConfigStoreType = {
  // @ts-ignore
  namespaced: true,

  state: {
    tableData: [],
    loading: false,
    tableName: 'parksConfig',
    sortingTable: {
      prop: null,
      propType: IApp.SortingPropType.String,
      direction: IApp.SortDirection.Default,
    },
    parkEvents: [],
  },

  getters: {
    sortedList: (state: IParksConfig.IState) => {
      return IAppTable.SortTableData({ tableData: state.tableData, sortingTable: state.sortingTable });
    },
  },

  mutations: {
    setProperty(state: IParksConfig.IState, { name, value }: { name: string, value: any}) { Vue.set(state, name, value); },
    setSubProperty(state: IParksConfig.IState, { block, name, value }: { block: string, name: string, value: any}) { Vue.set(state[block], name, value); },
    startLoading(state: IParksConfig.IState) { state.loading = true },
    stopLoading(state: IParksConfig.IState) { state.loading = false },
    setSorting(state: IParksConfig.IState, cell: IAppTable.ITableProp) {
      let sortingData = IAppTable.SetSorting(state.sortingTable, cell, state.tableName);
      Vue.set(state, 'sortingTable', sortingData);
    },

    addNewParkEvent(state: IParksConfig.IState) { state.parkEvents.unshift({...IParkEvent.DefaultParkEvent}); },
    clearNewParkEvent(state: IParksConfig.IState) { state.parkEvents.shift(); },
    updateParkEvent(state: IParksConfig.IState, payload: { index: number; parkEvent: IParkEvent.IEntity}) {
      Vue.set(state.parkEvents, payload.index, {...payload.parkEvent})
    },
    removeParkEvent(state: IParksConfig.IState, payload: { index: number; }) {
      state.parkEvents.splice(payload.index, 1);
    },
    addParkEventToList(state: IParksConfig.IState, payload: { parkEvent: IParkEvent.IEntity; }) {
      Vue.set(state.parkEvents, state.parkEvents.length, payload.parkEvent);
    },
  },

  actions: {
    async getTable({ commit, dispatch }: ParksConfigStoreType) {
      commit('startLoading');
      let asyncAction = async () => {
        const action = IParksConfigResource.Action.ParksMake;
        const params: any = { command: IParksConfigResource.Command.Get, };

        const { data: { result: { parks: tableData } }} = await httpResource.httpAction({ action, params });
        commit('setProperty', { name: 'tableData', value: tableData });
      };

      await dispatch('defaultResourceAction', { asyncAction }, { root: true });
      commit('stopLoading');
    },

    async getParkEvents({ commit, dispatch }: ParksConfigStoreType, payload: { parkId: number; filters?: IParkEvent.IFilterEvents; }) {
      commit('startLoading');
      let asyncAction = async () => {
        const action = IParkEvent.Action.FreeScheduleMake;
        const params: any = { command: IParkEvent.Command.Get, parkId: payload.parkId };

        if (payload.filters) {
          Object.entries(payload.filters).forEach(filterItem => {
            params[filterItem[0]] = filterItem[1];
          });
        }

        const { data: { result: { freeSchedule: tableData } }} = await httpResource.httpAction({ action, params });
        commit('setProperty', {
          name: 'parkEvents',
          value: tableData.map(item => {
            return {
              ...item,
              dateBeg: item.dateBeg ? moment(item.dateBeg).format(IApp.DateFormats.PrintedDateTime) : '',
              dateEnd: item.dateEnd ? moment(item.dateEnd).format(IApp.DateFormats.PrintedDateTime) : '',
            };
          })
        });
      };

      await dispatch('defaultResourceAction', { asyncAction }, { root: true });
      commit('stopLoading');
    },

    async saveParkEvent({ state, commit, dispatch }: ParksConfigStoreType, payload: IParkEvent.ISaveParkEventPayload) {
      commit('startLoading');
      let asyncAction = async () => {
        let formToSend = {...payload.formModel};
        Object.keys(payload.formModel).forEach(prop => {
          if (typeof(payload.formModel[prop]) !== 'boolean' && !payload.formModel[prop]) formToSend[prop] = null;
        });
        const params: any = {
          command: IParkEvent.Command.Set,
          ...formToSend,
        };
        if (!formToSend.id) params.parkId = payload.parkId;

        const response = await httpResource.httpAction({action: IParkEvent.Action.FreeScheduleMake, params});
        let index = payload.index;
        if (index === 0 && !formToSend.id && !state.parkEvents[0].id && state.parkEvents[1] && !state.parkEvents[1].id) {
          index = 1;
        }

        let resultEvent = response.data.result.freeSchedule[0];
        if (!payload.changeEnabled) {
          commit('updateParkEvent', {
            index,
            parkEvent: {
              ...resultEvent,
              dateBeg: resultEvent.dateBeg ? moment(resultEvent.dateBeg).format(IApp.DateFormats.PrintedDateTime) : '',
              dateEnd: resultEvent.dateEnd ? moment(resultEvent.dateEnd).format(IApp.DateFormats.PrintedDateTime) : '',
            },
          });
        }
        else {
          commit('removeParkEvent', { index });
        }
      };

      let response = await dispatch('defaultResourceAction', { asyncAction }, { root: true });
      commit('stopLoading');
      return response.success;
    },

    async addNewParkEvent({ state, commit, dispatch }: ParksConfigStoreType) {
      commit('addNewParkEvent');
      setTimeout(() => bus.$emit(IParkEvent.EmitterEvents.ActivateNewRow, 0));
    },
  }
};
